import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { gameConfig } from '../config/gameConfig.js';

/**
 * 模型加载器类
 * 用于预加载和缓存游戏中使用的3D模型
 */
export class ModelLoader {
    constructor() {
        // 模型缓存对象
        this.modelCache = {};
        
        // 初始化DRACO加载器
        this.dracoLoader = new DRACOLoader();
        this.dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
        
        // 初始化GLTF加载器
        this.gltfLoader = new GLTFLoader();
        this.gltfLoader.setDRACOLoader(this.dracoLoader);
        
        // 加载状态
        this.isLoading = false;
        this.loadingProgress = 0;
        this.totalModels = 0;
        this.loadedModels = 0;
    }
    
    /**
     * 预加载所有游戏中使用的模型
     * @returns {Promise} 所有模型加载完成后的Promise
     */
    async preloadAllModels() {
        if (this.isLoading) {
            console.warn('模型正在加载中，请勿重复调用');
            return;
        }
        
        this.isLoading = true;
        this.loadingProgress = 0;
        
        // 获取需要加载的模型路径
        const modelPaths = [
            gameConfig.dice.models.dice,      // 普通骰子模型
            gameConfig.dice.models.bomb,      // 炸弹模型
            gameConfig.dice.models.teleport   // 传送骰子模型
        ];
        
        // 过滤掉重复的模型路径
        const uniqueModelPaths = [...new Set(modelPaths)];
        this.totalModels = uniqueModelPaths.length;
        this.loadedModels = 0;
        
        console.log(`开始预加载 ${this.totalModels} 个模型...`);
        
        // 并行加载所有模型
        const loadPromises = uniqueModelPaths.map(path => this.loadModel(path));
        
        try {
            await Promise.all(loadPromises);
            console.log('所有模型预加载完成');
        } catch (error) {
            console.error('模型预加载过程中出错:', error);
        } finally {
            this.isLoading = false;
        }
    }
    
    /**
     * 加载单个模型并缓存
     * @param {string} path - 模型路径
     * @returns {Promise} 模型加载完成后的Promise
     */
    loadModel(path) {
        // 如果模型已经在缓存中，直接返回
        if (this.modelCache[path]) {
            console.log(`模型已在缓存中: ${path}`);
            return Promise.resolve(this.modelCache[path]);
        }
        
        console.log(`加载模型: ${path}`);
        
        return new Promise((resolve, reject) => {
            this.gltfLoader.load(
                path,
                (gltf) => {
                    // 缓存加载的模型
                    this.modelCache[path] = gltf.scene.clone();
                    
                    // 更新加载进度
                    this.loadedModels++;
                    this.loadingProgress = (this.loadedModels / this.totalModels) * 100;
                    console.log(`模型加载进度: ${this.loadingProgress.toFixed(2)}%`);
                    
                    resolve(this.modelCache[path]);
                },
                (progress) => {
                    const percentComplete = (progress.loaded / progress.total) * 100;
                    console.log(`模型 ${path} 加载进度: ${percentComplete.toFixed(2)}%`);
                },
                (error) => {
                    console.error(`加载模型出错 ${path}:`, error);
                    reject(error);
                }
            );
        });
    }
    
    /**
     * 获取模型（如果已缓存则从缓存获取，否则加载）
     * @param {string} path - 模型路径
     * @returns {Promise<THREE.Object3D>} 模型对象
     */
    async getModel(path) {
        if (!path) {
            console.error('模型路径不能为空');
            return null;
        }
        
        try {
            // 如果模型不在缓存中，则加载它
            if (!this.modelCache[path]) {
                await this.loadModel(path);
            }
            
            // 返回模型的克隆，以便每个实例可以独立修改
            return this.modelCache[path].clone();
        } catch (error) {
            console.error(`获取模型失败 ${path}:`, error);
            return null;
        }
    }
    
    /**
     * 清理加载器资源
     */
    dispose() {
        // 清理DRACO加载器
        if (this.dracoLoader) {
            this.dracoLoader.dispose();
        }
        
        // 清理模型缓存
        this.modelCache = {};
    }
    
    /**
     * 获取当前加载进度
     * @returns {number} 加载进度百分比 (0-100)
     */
    getLoadingProgress() {
        return this.loadingProgress;
    }
    
    /**
     * 检查是否所有模型都已加载完成
     * @returns {boolean} 是否所有模型都已加载完成
     */
    isAllModelsLoaded() {
        return this.loadedModels === this.totalModels && this.totalModels > 0;
    }
}

// 创建单例实例
const modelLoader = new ModelLoader();
export default modelLoader;